<template>
  <BaseInputText
    v-model="modelValue"
    v-bind="inputProps"
    new-design
  >
    <template
      v-if="hasSlotContent(slots.label)"
      #label
    >
      <slot name="label" />
    </template>

    <template
      v-if="hasSlotContent(slots.appendLabel)"
      #appendLabel
    >
      <slot name="appendLabel" />
    </template>

    <template
      v-if="hasSlotContent(slots.after)"
      #after
    >
      <slot name="after" />
    </template>

    <template #textboxAppend="{ focused }">
      <CharacterCounter
        v-if="shouldShowCounter(focused)"
        :characters="computedCharacterCount"
        data-testid="character-counter"
        :limit="characterLimit!"
        :placement="counterPlacement"
        :show-limit="showLimit"
        :size="characterCounterSize"
      />
    </template>
  </BaseInputText>
</template>

<script lang="ts">
import { type InputTextProps } from "shared/components/base/BaseInputText.vue";

interface InputTextWithCounterProps extends InputTextProps {
  characterCount?: number;
  characterCounterSize?: number;
  characterLimit?: number;
  counterPlacement?: "top-right" | "bottom-right";
  hideCharacterCounter?: boolean;
  inputClasses?: string | string[] | Record<string, boolean>;
  showCounterOnlyWhenFocused?: boolean;
  showLimit?: boolean;
}
</script>

<script setup lang="ts">
import { computed, useAttrs, useSlots } from "vue";

import BaseInputText from "shared/components/base/BaseInputText.vue";
import CharacterCounter from "shared/components/CharacterCounter.vue";
import { hasSlotContent } from "shared/helpers/hasSlotContent";

const modelValue = defineModel<string>({ default: "" });
const slots = useSlots();
const attrs = useAttrs();

const props = withDefaults(defineProps<InputTextWithCounterProps>(), {
  characterCount: undefined,
  characterCounterSize: 20,
  characterLimit: undefined,
  counterPlacement: "top-right",
  inputClasses: undefined,
  resize: "vertical",
  rows: 3,
  type: "textarea",
});

const inputProps = computed(() => {
  const {
    characterCount,
    characterCounterSize,
    characterLimit,
    counterPlacement,
    hideCharacterCounter,
    showCounterOnlyWhenFocused,
    showLimit,
    ...inputTextAttrsOnly
  } = props;

  return {
    ...attrs,
    ...inputTextAttrsOnly,
  };
});

const computedCharacterCount = computed<number>(
  () => props.characterCount ?? (modelValue.value || "").length
);

function shouldShowCounter(focused: boolean): boolean {
  if (!props.characterLimit || props.hideCharacterCounter) {
    return false;
  }

  if (props.showCounterOnlyWhenFocused) {
    return focused || computedCharacterCount.value > props.characterLimit;
  }

  return true;
}
</script>
